/**
 * Hook unificado de cotización para TODAS las calculadoras de {BRAND.name}.
 *
 * Pedido por Carlos (jun 22, 2026):
 * > "Para que las 3 calculadoras y el bot {BRAND.botName} sean iguales — cualquier fix
 * >  futuro debe hacerse en UN SOLO lugar."
 *
 * Lo que encapsula:
 *  - Debounce automático (espera 350ms a que el usuario termine de escribir).
 *  - Request ID para descartar respuestas viejas (race condition).
 *  - Llamadas al endpoint `/api/quote/calculate` (forward) y `/api/quote/reverse`.
 *  - El backend ya elige el `rounding` correcto por divisa (CLP=1000, BOB=10, ARS=100).
 *
 * Uso:
 *   const { quote, loading, calculateForward, calculateReverse, reset } = useQuote();
 *   calculateForward(20000, 'CLP', 'BOB');   // dispara cálculo con debounce
 *   // luego: quote.amount_destination → 204
 *
 * El bot {BRAND.botName} (backend WhatsApp) consume el mismo motor `reverse_quote_tiered`
 * y `calculate_transfer_*` del backend → consistencia 1:1 con las calculadoras web.
 */
import { useCallback, useRef, useState } from 'react';
import { quoteCalculate, quoteReverse } from '../utils/quoteClient';
import { BRAND } from '../brand';

const DEFAULT_DEBOUNCE_MS = 350;

export default function useQuote({ debounceMs = DEFAULT_DEBOUNCE_MS } = {}) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);
  const debounceTimerRef = useRef(null);

  const reset = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    requestIdRef.current++;
    setQuote(null);
    setLoading(false);
    setError(null);
  }, []);

  const _runForward = useCallback(async (amount, origin, destination) => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await quoteCalculate({ amount, origin, destination });
      if (reqId !== requestIdRef.current) return; // stale
      setQuote({ ...data, direction: 'forward', generatedAt: Date.now() });
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      console.warn('quoteCalculate falló', err?.message);
      setError(err);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, []);

  const _runReverse = useCallback(async (amountDestination, origin, destination) => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      // Sin `rounding` explícito → backend usa `rounding=1` (peso entero,
      // redondeo al MÁS CERCANO). Antes el default era 1000 hacia arriba →
      // producía sesgo del 3% en montos chicos (fix jul 23, 2026).
      const data = await quoteReverse({ amountDestination, origin, destination });
      if (reqId !== requestIdRef.current) return;
      setQuote({ ...data, direction: 'reverse', generatedAt: Date.now() });
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      console.warn('quoteReverse falló', err?.message);
      setError(err);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, []);

  const _debounce = useCallback((fn) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(fn, debounceMs);
  }, [debounceMs]);

  const calculateForward = useCallback((amount, origin, destination) => {
    const num = parseFloat(amount);
    if (!num || num <= 0) {
      reset();
      return;
    }
    _debounce(() => _runForward(num, origin, destination));
  }, [_debounce, _runForward, reset]);

  const calculateReverse = useCallback((amountDestination, origin, destination) => {
    const num = parseFloat(amountDestination);
    if (!num || num <= 0) {
      reset();
      return;
    }
    _debounce(() => _runReverse(num, origin, destination));
  }, [_debounce, _runReverse, reset]);

  return {
    quote,            // { amount_origin, amount_destination, effective_rate, direction, ... } | null
    loading,
    error,
    calculateForward, // (amount, origin, destination) → debounced
    calculateReverse, // (amountDestination, origin, destination) → debounced
    reset,
  };
}
